import { LicenseType, Feature, FEATURES } from './types';

// Define what each license includes
export const LICENSE_FEATURES: Record<LicenseType, Feature[]> = {
  unpickle_standard: [
    // Basic features only
    FEATURES.CUSTOM_LABELS,
    FEATURES.PROJECT_THEMES,
  ],
  unpickle_pro: [
    // All features
    ...Object.values(FEATURES),
  ],
};

// Numeric limits per license
export const LICENSE_LIMITS: Record<LicenseType, {
  maxProjects: number | null; // null = unlimited
  maxTasksPerProject: number | null;
  maxContributors: number | null;
  maxFileSize: number; // in MB
  maxStoragePerProject: number; // in MB
}> = {
  unpickle_standard: {
    maxProjects: 3,
    maxTasksPerProject: 50,
    maxContributors: 2,
    maxFileSize: 5, // 5MB for Standard users
    maxStoragePerProject: 50, // 50MB total storage per project for Standard
  },
  unpickle_pro: {
    maxProjects: null,
    maxTasksPerProject: null,
    maxContributors: null,
    maxFileSize: 50, // 50MB for Pro users
    maxStoragePerProject: 1024, // 1GB total storage per project for Pro
  },
};

// Human-readable feature descriptions
export const FEATURE_DESCRIPTIONS: Record<Feature, { name: string; description: string }> = {
  [FEATURES.CUSTOM_WIDGETS]: {
    name: 'Custom Widgets',
    description: 'Create custom dashboard widgets with charts and visualizations',
  },
  [FEATURES.DASHBOARD_TAB]: {
    name: 'Dashboard Tab',
    description: 'Access the dashboard view with analytics and insights',
  },
  [FEATURES.UNLIMITED_CONTRIBUTORS]: {
    name: 'Unlimited Contributors',
    description: 'Add unlimited team members to your projects',
  },
  [FEATURES.GUEST_VIEWERS]: {
    name: 'Guest Viewers',
    description: 'Invite view-only guests to your projects',
  },
  [FEATURES.FILE_ATTACHMENTS]: {
    name: 'File Attachments',
    description: 'Attach files to tasks and comments (5MB limit for Standard, 50MB for Pro)',
  },
  [FEATURES.EXPORT_DATA]: {
    name: 'Export Data',
    description: 'Export your project data to CSV or JSON',
  },
  [FEATURES.PROJECT_THEMES]: {
    name: 'Project Themes',
    description: 'Customize project colors and themes',
  },
  [FEATURES.CUSTOM_LABELS]: {
    name: 'Custom Labels',
    description: 'Create custom label options for status columns',
  },
  [FEATURES.ADVANCED_COLUMNS]: {
    name: 'Advanced Columns',
    description: 'Use advanced column types like Budget, Actual, and Currency',
  },
  [FEATURES.UNLIMITED_PROJECTS]: {
    name: 'Unlimited Projects',
    description: 'Create unlimited projects',
  },
  [FEATURES.UNLIMITED_TASKS]: {
    name: 'Unlimited Tasks',
    description: 'Add unlimited tasks per project',
  },
};

// Features that are actually implemented and should be shown in the upgrade UI
export const IMPLEMENTED_PRO_FEATURES: Feature[] = [
  FEATURES.UNLIMITED_PROJECTS,
  FEATURES.UNLIMITED_TASKS,
  FEATURES.UNLIMITED_CONTRIBUTORS,
  FEATURES.CUSTOM_WIDGETS,
  FEATURES.DASHBOARD_TAB,
  FEATURES.EXPORT_DATA,
  // FILE_ATTACHMENTS is available to all, but Pro gets 50MB vs 5MB
];

// Additional Pro benefits not in FEATURES enum
export const PRO_BENEFITS = [
  {
    name: '50MB File Uploads',
    description: '5x larger file upload limit vs Standard (10MB)',
    category: 'limits',
  },
  {
    name: 'Formula Columns',
    description: 'Create calculated columns with custom formulas',
    category: 'advanced',
  },
  {
    name: 'AI Chart Assistant',
    description: 'Let AI suggest and create analytics charts',
    category: 'ai',
  },
  {
    name: 'Priority Support',
    description: 'Get faster response times on support requests',
    category: 'support',
  },
];
